import { getPromotions, getTournaments, getUserBonuses } from '../api';
import { BONUS_SYSTEM_TYPE } from '../constants';
import getValidBonuses from '../utils/getValidBonuses';
import { Tournament } from './normalize/types';
import { parseTournaments } from './normalize';
import { usePromotionsStore } from './store';
import { Bonus, BonusDetails, DateHelper, PromotionsState } from './types';

export const actionsCreator = ({
  setState,
  getState
}: {
  setState: (state: Partial<PromotionsState>) => void;
  getState: () => PromotionsState;
}) => ({
  FETCH_PROMOTIONS: async (refetch = false) => {
    if (!refetch && getState().isPromosLoaded) {
      return;
    }

    const promotions = await getPromotions();

    setState({
      promotions,
      isPromosLoaded: true
    });
  },

  SET_TIMER_IS_COMPLETED: (isCompleted: boolean) => {
    setState({ isCompleted });
  },

  FETCH_TOURNAMENTS_SERVICE_INFO: async (ids: number[]) => {
    try {
      setState({ isTournamentsLoading: true });

      const { data }: { data?: { tournaments: Tournament[] } } =
        await getTournaments(ids);
      const parsedTournaments = parseTournaments(data?.tournaments || []);

      const currentState = usePromotionsStore.getState();
      const updatedState: Partial<PromotionsState> = {
        tournaments: {
          ...currentState.tournaments,
          ...parsedTournaments
        },
        isTournamentsLoaded: true,
        isTournamentsLoading: false
      };

      setState(updatedState);
    } catch {
      setState({ isTournamentsLoading: false, isTournamentsLoaded: true });
    }
  },

  RESET_TOURNAMENTS_SERVICE_INFO: () => {
    setState({
      tournaments: [],
      isTournamentsLoaded: false,
      isTournamentsLoading: false
    });
  },

  FETCH_USER_PARTICIPATED_BONUSES: async ({
    promoBonuses,
    f
  }: {
    promoBonuses: Bonus[];
    f: DateHelper;
  }) => {
    try {
      const bonusesTemplateIds = promoBonuses.reduce(
        (
          acc: { crmBonusTemplateIds: number[]; bsBonusTemplateIds: number[] },
          { bonusTemplateId, system }
        ) => {
          if (system === BONUS_SYSTEM_TYPE.CRM) {
            acc.crmBonusTemplateIds.push(Number(bonusTemplateId));
          } else {
            acc.bsBonusTemplateIds.push(Number(bonusTemplateId));
          }

          return acc;
        },
        { crmBonusTemplateIds: [], bsBonusTemplateIds: [] }
      );

      const userParticipatedBonuses = await getUserBonuses(bonusesTemplateIds);
      const normalizedUserBonuses = userParticipatedBonuses.map(
        ({ type, data }) => ({
          ...data,
          bonusSourceType: type,
          bonusTemplateId: data.bonusTemplateId || data.bonusModelId,
          bonusTime: data.bonusTime || data.freespinTime
        })
      );

      promotionsActions.SET_PROMO_BONUSES({
        promoBonuses,
        userParticipatedBonuses: normalizedUserBonuses,
        f
      });
    } catch (e) {
      console.warn(e);
      promotionsActions.SET_PROMO_BONUSES({ promoBonuses, f });
    } finally {
      setState({
        isPromoBonusesLoaded: true
      });
    }
  },

  SET_PROMO_BONUSES: ({
    promoBonuses = [],
    userParticipatedBonuses = [],
    f
  }: {
    promoBonuses: Bonus[];
    userParticipatedBonuses?: BonusDetails[];
    f: DateHelper;
  }) => {
    setState({
      bonuses: getValidBonuses(promoBonuses, userParticipatedBonuses, f),
      isPromoBonusesLoaded: true
    });
  }
});

export const promotionsActions = actionsCreator(usePromotionsStore);
